import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Spinner } from './primitives.js';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'success'
  | 'subtle';

export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Accent surface. `pathway` tints the primary variant so the same button
 * component carries master / personal-training / boxing identity without each
 * experience forking the component.
 */
export type ButtonPathway = 'master' | 'pt' | 'boxing';

export interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  pathway?: ButtonPathway;
  /** Shows a spinner, sets `aria-busy` and blocks activation. */
  loading?: boolean;
  /** Accessible label announced while loading. */
  loadingLabel?: string;
  fullWidth?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const BASE =
  'relative inline-flex items-center justify-center gap-2 rounded-xl font-medium tracking-tight transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out select-none ' +
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-near-black ' +
  'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none ' +
  'motion-safe:active:translate-y-px';

const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-sm',
  md: 'min-h-11 px-5 text-sm',
  lg: 'min-h-12 px-7 text-base',
};

const PATHWAY_PRIMARY: Record<ButtonPathway, string> = {
  master:
    'bg-warm-white text-near-black hover:bg-white focus-visible:ring-warm-white shadow-[0_1px_0_0_rgba(255,255,255,0.4)_inset]',
  // Deep fills, not the bright brand accents: white on `#168cff` / `#e3373d`
  // fails WCAG AA (~3.4:1). The deep tokens clear 4.5:1 while staying on-brand.
  pt: 'bg-accent-pt-deep text-white hover:bg-accent-pt focus-visible:ring-accent-pt shadow-[0_0_24px_-6px_var(--color-accent-pt)]',
  boxing:
    'bg-accent-boxing-deep text-white hover:bg-accent-boxing focus-visible:ring-accent-boxing shadow-[0_0_24px_-6px_var(--color-accent-boxing)]',
};

const PATHWAY_RING: Record<ButtonPathway, string> = {
  master: 'focus-visible:ring-warm-white',
  pt: 'focus-visible:ring-accent-pt',
  boxing: 'focus-visible:ring-accent-boxing',
};

function variantClasses(variant: ButtonVariant, pathway: ButtonPathway): string {
  switch (variant) {
    case 'primary':
      return PATHWAY_PRIMARY[pathway];
    case 'secondary':
      return cn(
        'border border-white/25 bg-white/[0.04] text-warm-white backdrop-blur-sm hover:border-white/45 hover:bg-white/[0.09]',
        PATHWAY_RING[pathway],
      );
    case 'ghost':
      return cn(
        'text-warm-white/85 hover:bg-white/[0.07] hover:text-warm-white',
        PATHWAY_RING[pathway],
      );
    case 'subtle':
      return cn(
        'bg-graphite text-platinum hover:bg-graphite-light hover:text-warm-white',
        PATHWAY_RING[pathway],
      );
    case 'danger':
      return 'bg-accent-boxing-deep text-white hover:bg-accent-boxing focus-visible:ring-accent-boxing';
    case 'success':
      return 'bg-performance-green text-near-black hover:brightness-110 focus-visible:ring-performance-green';
  }
}

export type ButtonProps = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * The single button implementation.
 *
 * Every button in the platform performs an action or navigates. There is no
 * decorative or disabled-forever variant: if a capability is not available, the
 * calling screen removes the control rather than rendering an inert one.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    pathway = 'master',
    loading = false,
    loadingLabel = 'Working',
    fullWidth = false,
    iconLeft,
    iconRight,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled === true || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        BASE,
        SIZES[size],
        variantClasses(variant, pathway),
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} label={loadingLabel} />
      ) : (
        iconLeft && (
          <span aria-hidden="true" className="shrink-0">
            {iconLeft}
          </span>
        )
      )}
      <span className={cn(loading && 'opacity-70')}>{children}</span>
      {!loading && iconRight && (
        <span aria-hidden="true" className="shrink-0">
          {iconRight}
        </span>
      )}
    </button>
  );
});

/**
 * Shared class builder so a router `<Link>` can be styled identically to a
 * button without this package importing the router.
 */
export function buttonClassName(options: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  pathway?: ButtonPathway;
  fullWidth?: boolean;
  className?: string;
}): string {
  const {
    variant = 'primary',
    size = 'md',
    pathway = 'master',
    fullWidth = false,
    className,
  } = options;
  return cn(BASE, SIZES[size], variantClasses(variant, pathway), fullWidth && 'w-full', className);
}
